import { compareAsc, parseISO } from 'date-fns';
import * as React from 'react';

import { Api } from '../../api';
import { Stack } from '../../components';
import TimelineComment from '../client-tickets/components/TimelineComment';
import { groupTimelineItems } from '../client-tickets/util';
import CommentComposer, { type ComposerActionHelpers } from './CommentComposer';

type TicketEvent = Api.Ticket.V_Detail['events'][number];
type TicketComment = Api.Ticket.V_Detail['comments'][number];

/**
 * A comment as the shared thread reads it: the ticket `Comment` and the
 * wishlist idea comment carry the same fields, differing only in `@type`.
 */
export type ThreadComment = {
  '@id': string;
  '@type': string;
  id: string;
  body: string;
  isInternal: boolean;
  createdAt: string;
  author: Api.User.BaseView;
  uploads: Array<Api.Upload.BaseView>;
};

export type CommentsThreadEntity = {
  body: string | null;
  uploads: Array<Api.Upload.BaseView>;
  author: Api.User.BaseView | null;
  createdAt: string;
  comments: Array<ThreadComment>;
  /** Ticket-only; wishlist ideas have no timeline events. */
  events?: Array<TicketEvent>;
};

type ComposerConfig = {
  endpoint: string;
  relationKey: string;
  relationValue: string;
  extraControls?: React.ReactNode;
  extraActions?: (helpers: ComposerActionHelpers) => React.ReactNode;
  onPosted?: () => void | Promise<void>;
};

type Props = {
  entity: CommentsThreadEntity;
  showInternal: boolean;
  invalidate: () => unknown;
  composer: ComposerConfig;
  /** Renders a grouped batch of ticket events; tickets only. */
  renderEvents?: (events: Array<TicketEvent>) => React.ReactNode;
};

/**
 * The comment timeline shared by the ticket and wishlist detail screens:
 * the opening post, the visible comments (and ticket events), and the composer.
 */
export default function CommentsThread({
  entity,
  showInternal,
  invalidate,
  composer,
  renderEvents,
}: Props) {
  const timelineItems = [...entity.comments, ...(entity.events ?? [])]
    .filter((item) => showInternal || !item.isInternal)
    .sort((a, b) => compareAsc(parseISO(a.createdAt), parseISO(b.createdAt)));

  const timelineGroups = entity.events
    ? groupTimelineItems(timelineItems as Array<TicketComment | TicketEvent>)
    : null;

  return (
    <Stack gap={1.5}>
      <TimelineComment
        body={entity.body}
        uploads={entity.uploads}
        isInternal={false}
        author={entity.author}
        createdAt={entity.createdAt}
      />

      {timelineGroups
        ? timelineGroups.map((group) => {
            const first = group[0];
            if (first['@type'] === 'Comment') {
              return <TimelineComment key={first.id} {...first} />;
            }
            return (
              <React.Fragment key={first.id}>
                {renderEvents?.(group as Array<TicketEvent>)}
              </React.Fragment>
            );
          })
        : entity.comments
            .filter((comment) => showInternal || !comment.isInternal)
            .map((comment) => (
              <TimelineComment key={comment.id} {...comment} />
            ))}

      <CommentComposer
        endpoint={composer.endpoint}
        relationKey={composer.relationKey}
        relationValue={composer.relationValue}
        invalidate={invalidate}
        extraControls={composer.extraControls}
        extraActions={composer.extraActions}
        onPosted={composer.onPosted}
      />
    </Stack>
  );
}
